import { ArrowRight } from "lucide-react";
import { useId, useState, type FormEvent } from "react";
import {
  EarlyAccessError,
  isEmailAddress,
  submitEarlyAccess,
} from "../api/earlyAccess";
import { useMessages } from "../i18n";
import { LANGUAGE_LABELS, type SheetSettings } from "../shared";
import { Button } from "./ui/Button";

/**
 * The pair as the list reads it, e.g. "Thai to Dutch".
 *
 * Deliberately English whatever language the form was filled in: this string is
 * read by whoever reads the mailing list, not by the person who typed it.
 */
export function describeLanguages(settings: SheetSettings): string {
  return `${LANGUAGE_LABELS[settings.left]} to ${LANGUAGE_LABELS[settings.right]}`;
}

interface EarlyAccessFormProps {
  /** The pair and the pace travel with the address, so the list is worth reading. */
  settings: SheetSettings;
  /** The day the learner was standing on when they were asked. */
  day: number;
  submitLabel: string;
  onJoined: (email: string) => void;
  /** Told whenever the list refuses, so a caller can offer a way past it. */
  onError?: (message: string | null) => void;
}

/**
 * The one place an email address is asked for. The homepage and the week gate
 * both mount it, so there is a single form, a single endpoint, and a single
 * set of rules about what gets sent.
 */
export function EarlyAccessForm({
  settings,
  day,
  submitLabel,
  onJoined,
  onError,
}: EarlyAccessFormProps) {
  const errorId = useId();
  const t = useMessages();
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fail = (message: string | null) => {
    setError(message);
    onError?.(message);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (sending) return;

    if (!isEmailAddress(email)) {
      fail(t.app.emailInvalid);
      return;
    }

    setSending(true);
    fail(null);

    try {
      await submitEarlyAccess({
        email,
        languages: describeLanguages(settings),
        day,
        words: settings.words,
        sentences: settings.sentences,
        honeypot,
      });
      onJoined(email);
    } catch (cause) {
      fail(
        cause instanceof EarlyAccessError
          ? cause.message
          : t.app.sendFailed,
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <form className="signup" onSubmit={submit} noValidate>
      <label className="field">
        <span>{t.app.email}</span>
        <input
          type="email"
          name="email"
          value={email}
          autoComplete="email"
          placeholder={t.app.emailPlaceholder}
          aria-invalid={error !== null}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => {
            setEmail(event.target.value);
            if (error) fail(null);
          }}
        />
      </label>

      {/* Bots fill everything; Formspree drops whatever arrives here. */}
      <input
        className="signup__trap"
        type="text"
        name="_gotcha"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={honeypot}
        onChange={(event) => setHoneypot(event.target.value)}
      />

      {error ? (
        <p className="signup__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}

      <Button
        type="submit"
        size="lg"
        icon={<ArrowRight size={19} />}
        disabled={sending}
      >
        {sending ? t.app.sending : submitLabel}
      </Button>
    </form>
  );
}
